import type { LocationInput, LocationItem } from "@vaulted/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { errorMessage, get, post, put } from "../../api";
import { AddressField } from "../../components/AddressField";
import { Alert, Badge, Button, Checkbox, Field, Loading, Panel, PanelHeader } from "../../components/ui";
import { AddButton, RemoveButton, SettingsForm } from "./common";

const DAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const WEEK_ORDER = [1, 2, 3, 4, 5, 6, 0];

const blank: LocationInput = {
  name: "",
  addressLine1: "",
  addressLine2: "",
  city: "",
  state: "",
  postalCode: "",
  phone: null,
  taxRatePercent: 0,
  active: true,
  hours: [],
  closures: [],
};

export function LocationsTab() {
  const locations = useQuery({ queryKey: ["locations"], queryFn: () => get<LocationItem[]>("/api/locations") });
  const [adding, setAdding] = useState(false);

  if (locations.isLoading) return <Loading />;
  if (locations.error) return <Alert>{errorMessage(locations.error)}</Alert>;
  const list = locations.data ?? [];

  return (
    <div className="flex flex-col gap-6">
      {list.length === 0 && !adding && <Alert tone="info">No locations yet. Add your clinic's address and hours.</Alert>}
      {list.map((l) => (
        <LocationForm key={l.id} location={l} />
      ))}
      {adding ? (
        <LocationForm onDone={() => setAdding(false)} />
      ) : (
        <div>
          <Button onClick={() => setAdding(true)}>Add location</Button>
        </div>
      )}
    </div>
  );
}

function LocationForm({ location, onDone }: { location?: LocationItem; onDone?: () => void }) {
  const qc = useQueryClient();
  const initial: LocationInput = location ? { ...location } : blank;
  const [v, setV] = useState<LocationInput>(initial);
  const [notice, setNotice] = useState<string | null>(null);
  const set = <K extends keyof LocationInput>(k: K, value: LocationInput[K]) => {
    setNotice(null);
    setV((cur) => ({ ...cur, [k]: value }));
  };
  const save = useMutation({
    mutationFn: () => (location ? put<LocationItem>(`/api/locations/${location.id}`, v) : post<LocationItem>("/api/locations", v)),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["locations"] });
      setNotice("Location saved.");
      onDone?.();
    },
  });
  const dirty = JSON.stringify(v) !== JSON.stringify(initial);

  const addPeriod = (weekday: number) => {
    const same = v.hours.filter((h) => h.weekday === weekday);
    const last = same[same.length - 1];
    set("hours", [...v.hours, last ? { weekday, opens: last.closes, closes: last.closes < "20:00" ? "20:00" : "23:00" } : { weekday, opens: "09:00", closes: "17:00" }]);
  };

  return (
    <Panel>
      <PanelHeader
        title={location ? location.name : "New location"}
        description={location ? `${location.addressLine1}, ${location.city}` : undefined}
        action={location && !location.active ? <Badge tone="off">Inactive</Badge> : undefined}
      />
      <SettingsForm onSave={() => save.mutate()} saving={save.isPending} error={save.error} notice={notice} dirty={dirty} label={location ? "Save location" : "Add location"}>
        <Field label="Name" required value={v.name} onChange={(e) => set("name", e.target.value)} />
        <div className="grid gap-4 sm:grid-cols-2">
          <AddressField
            label="Street address"
            required
            placeholder=""
            value={v.addressLine1}
            onChange={(line1) => set("addressLine1", line1)}
            onResolved={(r) => setV((cur) => ({ ...cur, addressLine1: r.line1, city: r.city, state: r.state, postalCode: r.postalCode, ...(r.line2 ? { addressLine2: r.line2 } : {}) }))}
          />
          <Field label="Suite or unit (optional)" value={v.addressLine2} onChange={(e) => set("addressLine2", e.target.value)} />
        </div>
        <div className="grid gap-4 sm:grid-cols-[2fr_1fr_1fr]">
          <Field label="City" required value={v.city} onChange={(e) => set("city", e.target.value)} />
          <Field label="State" required value={v.state} onChange={(e) => set("state", e.target.value)} />
          <Field label="ZIP" required value={v.postalCode} onChange={(e) => set("postalCode", e.target.value)} />
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Phone (optional)" type="tel" value={v.phone ?? ""} onChange={(e) => set("phone", e.target.value || null)} />
          <Field
            label="Sales tax rate (%)"
            type="number"
            min={0}
            max={25}
            step={0.001}
            value={v.taxRatePercent}
            onChange={(e) => set("taxRatePercent", Number(e.target.value))}
            hint="Applied to items marked taxable. 0 if you don't charge sales tax."
          />
        </div>

        <fieldset>
          <legend className="mb-1 text-sm font-semibold text-ink-soft">Weekly hours</legend>
          <p className="mb-2 text-sm text-muted">A day can have more than one open period. Days with no hours are closed.</p>
          <div className="divide-y divide-mist rounded-[var(--radius-control)] border border-line">
            {WEEK_ORDER.map((d) => {
              const periods = v.hours.map((h, i) => ({ ...h, i })).filter((h) => h.weekday === d);
              return (
                <div key={d} className="flex flex-wrap items-start gap-x-4 gap-y-2 px-3 py-2.5">
                  <div className="w-24 pt-1.5 text-sm font-semibold">{DAYS[d]}</div>
                  <div className="flex flex-1 flex-col gap-2">
                    {periods.length === 0 && <span className="pt-1.5 text-sm text-muted">Closed</span>}
                    {periods.map((p) => (
                      <div key={p.i} className="flex items-center gap-2">
                        <TimeInput label={`${DAYS[d]} opens`} value={p.opens} onChange={(t) => set("hours", v.hours.map((h, j) => (j === p.i ? { ...h, opens: t } : h)))} />
                        <span className="text-muted">to</span>
                        <TimeInput label={`${DAYS[d]} closes`} value={p.closes} onChange={(t) => set("hours", v.hours.map((h, j) => (j === p.i ? { ...h, closes: t } : h)))} />
                        <RemoveButton label="Remove period" onClick={() => set("hours", v.hours.filter((_, j) => j !== p.i))} />
                      </div>
                    ))}
                  </div>
                  <button type="button" className="pt-1.5 text-sm font-semibold text-brand hover:text-brand-deep" onClick={() => addPeriod(d)}>
                    {periods.length ? "Add hours" : "Open"}
                  </button>
                </div>
              );
            })}
          </div>
        </fieldset>

        <fieldset className="flex flex-col gap-2">
          <legend className="mb-1 text-sm font-semibold text-ink-soft">Closed dates</legend>
          {v.closures.length === 0 && <p className="text-sm text-muted">Holidays and other days you're closed. Managers can also close a day from the Availability page.</p>}
          {v.closures.map((c, i) => (
            <div key={i} className="flex flex-wrap items-center gap-2">
              <input
                type="date"
                aria-label="Closed date"
                required
                className="rounded-[var(--radius-control)] border border-line bg-white px-2 py-1.5"
                value={c.date}
                onChange={(e) => set("closures", v.closures.map((x, j) => (j === i ? { ...x, date: e.target.value } : x)))}
              />
              <input
                aria-label="Reason"
                placeholder="Reason (optional)"
                className="min-w-0 flex-1 rounded-[var(--radius-control)] border border-line bg-white px-2 py-1.5"
                value={c.label}
                onChange={(e) => set("closures", v.closures.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))}
              />
              <select
                aria-label="How long"
                className="rounded-[var(--radius-control)] border border-line bg-white px-2 py-1.5 text-sm"
                value={c.start === null ? "all" : "part"}
                onChange={(e) => set("closures", v.closures.map((x, j) => (j === i ? { ...x, start: e.target.value === "all" ? null : "12:00", end: e.target.value === "all" ? null : "17:00" } : x)))}
              >
                <option value="all">All day</option>
                <option value="part">Part of the day</option>
              </select>
              {c.start !== null && (
                <>
                  <TimeInput label="Closed from" value={c.start} onChange={(t) => set("closures", v.closures.map((x, j) => (j === i ? { ...x, start: t } : x)))} />
                  <span className="text-muted">to</span>
                  <TimeInput label="Closed until" value={c.end ?? "17:00"} onChange={(t) => set("closures", v.closures.map((x, j) => (j === i ? { ...x, end: t } : x)))} />
                </>
              )}
              <RemoveButton label="Remove date" onClick={() => set("closures", v.closures.filter((_, j) => j !== i))} />
            </div>
          ))}
          <AddButton onClick={() => set("closures", [...v.closures, { date: "", label: "", start: null, end: null }])}>Add closed date</AddButton>
        </fieldset>

        {location && (
          <Checkbox label="Active" description="Inactive locations can't be booked." checked={v.active} onChange={(on) => set("active", on)} />
        )}
        {onDone && (
          <div>
            <Button type="button" variant="quiet" onClick={onDone}>
              Cancel
            </Button>
          </div>
        )}
      </SettingsForm>
    </Panel>
  );
}

function TimeInput({ value, onChange, label }: { value: string; onChange: (t: string) => void; label: string }) {
  return (
    <input
      type="time"
      step={900}
      aria-label={label}
      required
      className="rounded-[var(--radius-control)] border border-line bg-white px-2 py-1.5 text-[15px]"
      value={value}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}
